import { find } from "lodash-es";
import { Component, type ReactElement } from "react";

import CheckboxField, { CheckboxReduxFormField } from "../../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../../core/redux-form/ReactSelectField.js";
import { ParamValues } from "./BlastCommon.js";
import { BLAST_MAX_EVALUE, BLAST_MAX_HSPS } from "./BlastInputForm.js";
import { type ISelectOption } from "@/app-utils/form.ts";
import { BLAST_METHOD } from "@/settings/settings.ts";

export interface IBlastAdvancedPanelProps {
  blastMethod?: string;
  currentMatchMismatchScore: ISelectOption;
  currentMatrix: ISelectOption;
  currentGapCosts: ISelectOption;
  currentWordSize: ISelectOption;
  currentGeneticCode: ISelectOption;
  handleChangeState: any;
}

function currentValueInOptionList(currentValue: number | string, options: ISelectOption[]) {
  return find(options, (o) => {
    return o.value === currentValue;
  });
}

const FORM_KEYS = ParamValues.FORM_KEYS;

/**
 * Blast advanced parameters panel to be used in context of redux-form
 */

class BlastAdvancedPanel extends Component<IBlastAdvancedPanelProps> {
  private wordSizeOptions;
  private gapCostOptions;

  constructor(props: IBlastAdvancedPanelProps) {
    super(props);
  }

  initOptions(props: IBlastAdvancedPanelProps) {
    /*
     * If we are switching between blastn/blastp forms or modify some params, the current selection of depending params
     * may not be valid anymore (it is missing in the new option list). In this case we must set it to a valid default again.
     *
     * Therefore, we first determine the correct option list for wordSize and gapCost,
     * then adapt the default selection in the redux-form state if necessary.
     *
     * CAUTION: There is potential for an infinite loop here if the defaults
     * defined in the ParamValues class are not contained in the actual option lists!
     */

    this.wordSizeOptions = ParamValues.getWordSizeOptions(props.blastMethod);
    this.gapCostOptions = ParamValues.getGapCostOptions(
      props.blastMethod,
      props.blastMethod === BLAST_METHOD.BLASTN ? props.currentMatchMismatchScore : props.currentMatrix,
    );

    if (!currentValueInOptionList(props.currentWordSize?.value, this.wordSizeOptions)) {
      if (props.blastMethod === BLAST_METHOD.BLASTN) {
        props.handleChangeState(
          FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD,
          ParamValues.nucleotideWordSizeDefault,
        );
      } else {
        props.handleChangeState(
          FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD,
          ParamValues.proteinWordSizeDefault,
        );
      }
    }

    if (!currentValueInOptionList(props.currentGapCosts?.value, this.gapCostOptions)) {
      if (this.isSearchOnProteinLevel(props)) {
        props.handleChangeState(
          FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD,
          ParamValues.proteinGapCostDefaults[props.currentMatrix?.value],
        );
      } else {
        if (
          props.currentMatchMismatchScore &&
          props.currentMatchMismatchScore.value === ParamValues.fourMinusFiveMatchMismatchScoreOption
        ) {
          props.handleChangeState(
            FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD,
            ParamValues.fourMinusFiveGapCostDefault,
          );
        } else {
          props.handleChangeState(
            FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD,
            ParamValues.nucleotideGapCostDefault,
          );
        }
      }
    }
  }

  // TODO this is not optimal at all !
  componentDidUpdate(_prevProps: IBlastAdvancedPanelProps) {
    this.initOptions(this.props);
  }

  componentDidMount() {
    this.initOptions(this.props);
  }

  validateEValue(value) {
    return !value
      ? "A number is required."
      : value <= 0 || BLAST_MAX_EVALUE < value
        ? "A number larger than 0 and at most " + BLAST_MAX_EVALUE + " is required."
        : undefined;
  }

  validateHspCount(value) {
    return !value
      ? "An integer is required here."
      : value <= BLAST_MAX_HSPS && value >= 1
        ? undefined
        : "An integer larger than 0 and at most " + BLAST_MAX_HSPS + " is required here.";
  }

  isSearchOnProteinLevel = (props: IBlastAdvancedPanelProps): boolean => {
    return (
      props.blastMethod === BLAST_METHOD.BLASTP ||
      props.blastMethod === BLAST_METHOD.BLASTX ||
      props.blastMethod === BLAST_METHOD.TBLASTN
    );
  };

  render(): ReactElement {
    const matchMismatchScoreField: ReactElement =
      this.props.blastMethod === BLAST_METHOD.BLASTN ? (
        <ReactSelectReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATCH_MISMATCH_SCORE_FIELD}
          label="Match/Mismatch scores"
          options={ParamValues.matchMismatchScoreOptions}
          component={ReactSelectField}
          isMulti={false}
          horizontalLayout={false}
          isClearable={false}
        />
      ) : null;

    const matrixField: ReactElement = this.isSearchOnProteinLevel(this.props) ? (
      <ReactSelectReduxFormField
        name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MATRIX_FIELD}
        label="Matrix"
        options={ParamValues.matrixOptions}
        component={ReactSelectField}
        isMulti={false}
        horizontalLayout={false}
        isClearable={false}
      />
    ) : null;

    const compositionalAdjustmentsField: ReactElement = this.isSearchOnProteinLevel(this.props) ? (
      <ReactSelectReduxFormField
        name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_COMPOSITIONAL_ADJUSTMENTS_FIELD}
        label="Compositional adjustments"
        options={ParamValues.compositionalAdjustmentOptions}
        component={ReactSelectField}
        isMulti={false}
        horizontalLayout={false}
        isClearable={false}
      />
    ) : null;

    const genCodeField: ReactElement =
      this.props.blastMethod === BLAST_METHOD.BLASTX || this.props.blastMethod === BLAST_METHOD.TBLASTN ? (
        <ReactSelectReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GENETIC_CODE_FIELD}
          label="Genetic code"
          options={ParamValues.genCodeTableOptions}
          component={ReactSelectField}
          isMulti={false}
          horizontalLayout={false}
          isClearable={false}
        />
      ) : null;

    return (
      <div>
        <ReactSelectReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_TARGET_SEQ_FIELD}
          label="Max target sequences"
          options={ParamValues.maxTargetSeqOptions}
          component={ReactSelectField}
          isMulti={false}
          horizontalLayout={false}
          isClearable={false}
          isSearchable={false}
        />

        <CheckboxReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_SHORT_QUERY_FIELD}
          label="Automatically adjust parameters for short input sequences"
          component={CheckboxField}
          horizontalLayout={false}
          leftLabel="Short Queries"
          className="normal-label-wrap"
        />

        <InputReduxFormField
          componentClass="input"
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_EXPECT_THRESHOLD_FIELD}
          label="Expect threshold"
          component={InputField}
          horizontalLayout={false}
          type={"number"}
          parse={(value) => Number(value)}
          validate={this.validateEValue}
        />

        <ReactSelectReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_WORD_SIZE_FIELD}
          label="Word size"
          options={this.wordSizeOptions}
          component={ReactSelectField}
          isMulti={false}
          horizontalLayout={false}
          isClearable={false}
          isSearchable={false}
        />

        <InputReduxFormField
          componentClass="input"
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MAX_MATCHES_FIELD}
          label="Max matches in a query range"
          maxLength={100}
          component={InputField}
          horizontalLayout={false}
          type={"number"}
          parse={(value) => Number(value)}
          validate={this.validateHspCount}
        />

        {matchMismatchScoreField}
        {matrixField}
        {genCodeField}

        <ReactSelectReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_GAP_COST_FIELD}
          label="Gap costs"
          options={this.gapCostOptions}
          component={ReactSelectField}
          isMulti={false}
          horizontalLayout={false}
          isClearable={false}
        />

        {compositionalAdjustmentsField}

        <CheckboxReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_FILTER_LOW_COMPLEXITY_FIELD}
          label="Filter low complexity regions"
          component={CheckboxField}
          horizontalLayout={false}
          leftLabel="Filter low complexity regions"
        />

        <CheckboxReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_TABLE_FIELD}
          label="Mask for lookup table only"
          component={CheckboxField}
          horizontalLayout={false}
          leftLabel="Mask for lookup table only"
        />

        <CheckboxReduxFormField
          name={FORM_KEYS.ADVANCED.BLAST_INPUT_FORM_MASK_LOWER_FIELD}
          label="Mask lower case letters"
          component={CheckboxField}
          horizontalLayout={false}
          leftLabel="Mask lower case letters"
        />
      </div>
    );
  }
}

export default BlastAdvancedPanel;
